/* Global session/user data access manager/container.
 * ============================================================================
 *
 * This contains general session data across apps. Other app-specific globals
 * should be put in their respective app's globals file instead.
 *
 * Note that we use the StoredGlobal's "private" _cachedData member in this code
 * to get direct access to values for efficiency reasons, otherwise we'd have to
 * call get() which creates a copy. This is ok here because we know what the
 * data is and we wont let it be mutated directly (we just use the values).
 */

import { StoredGlobal } from '../libs/StoredGlobal.js';
import { api } from '../api/api.js';
import { log } from './log.js';

const STORAGEKEY_USERREFRESHED = 'lag:session.userRefreshedMS';
const MINIMUM_REFRESHPERIOD = 60000; // 60s

let hUserRefreshTimeout; // Handle/ID of refresh timeout.

export const session = {
    user: new class extends StoredGlobal {
        constructor() { super('user'); }

        get uniqueID() { return this._cachedData && this._cachedData.uniqueID; }
        get accountID() { return this._cachedData && this._cachedData.accountID; }
        get isSuper() { return !!(this._cachedData && this._cachedData.isSuper); }
        get displayName() { return this._cachedData && this._cachedData.displayName; }
        get preferences() { return (this._cachedData && this._cachedData.preferences) || {}; }
        // refreshPeriodMS: don't need app to access this piece of data, it is handled in code below.
        // permissions: don't need app to access this piece of data directly, but can use checkPermission method.

        /**
         * Check if the specified permission lookup is allowed for the current user.
         * This call should NOT be considered authorative, nor secure.
         * Any secure operation must be checked server side (and possibly in Database),
         * this check method simply allows us to avoid going to the server by knowing if permission is denied.
         * @param {String} lookup - The permission lookup to check for this current user.
         * @param {Boolean} [logWhenDenied] - If true specified then a denial will be logged to error log.
         */
        checkPermission(lookup, logWhenDenied) {
            const granted = _checkPermission(lookup, this._cachedData && this._cachedData.permissions);

            if (logWhenDenied && !granted) {
                log.warning(`session.user.checkPermission:DENIED (${lookup})`);
            }

            return granted;
        }
    },

    /**
    * Refresh the user session with provided parameters.
    * This is useful to set certain preferences (via params) such as accountID (for superAdmins).
    * @param {Object} params - Bag of params to pass to the auth refresh API.
    */
    refresh(params) {
        return _refreshUser(true, params);
    },
    /**
     * Sign-out (via API) and clear the stored session user data,
     * this will cause any change event listeners on the user to be triggered (assuming user isn't already signed-out).
     */
    signOut() {
        api.auth.signOut()
            .finally(_ => { session.user.set(null); });
    },
    signInTransferToken(userId, transferToken) {
        return api.auth.signInTransferToken(userId, transferToken)
            .then(_handleSignInResponse)
            .catch(_handleSignInException);
    },
    signInAdmin(username, password) {
        return api.auth.signInAdmin(username, password)
            .then(_handleSignInResponse)
            .catch(_handleSignInException);
    },
    signIn(username, password) {
        return api.auth.signInAdmin(username, password)
            .then(_handleSignInResponse)
            .catch(_handleSignInException);
    }
};

// Handle unauthorized event which is fired by api.auth.
window.addEventListener('api.auth.exceptions.UnauthorizedException', () => { session.user.set(null); });

function _handleSignInResponse(result) {
    if (result.ok) {
        session.user.set(result.data);
    }
    else {
        session.user.set(null);
    }

    return result;
}
function _handleSignInException(ex) {
    api.auth.signOut();
    session.user.set(null);
    throw ex;
}

function _refreshUser(forceRefresh, params) {
    // No user info, no point in trying to refresh, just resolve.
    if (!session.user.valid) {
        localStorage.removeItem(STORAGEKEY_USERREFRESHED);
        return Promise.resolve(false);
    }
    else if (forceRefresh) {
        localStorage.removeItem(STORAGEKEY_USERREFRESHED);
    }

    // Calculate ellapsed time since last refresh.
    const currentMS = Date.now();
    const ellapsedMS = currentMS - (localStorage.getItem(STORAGEKEY_USERREFRESHED) || 0);

    // The threshold when we want to refresh. We use the half the refresh period as specified by AuthAPI.
    let refreshWhenEllapsed = (session.user._cachedData.refreshPeriodMS || 0) / 2;
    // Check refresh threshold is not below some small period just to protect against refreshPeriodMS being
    // super small by accident and causing crazy refresh cycles.
    if (refreshWhenEllapsed < MINIMUM_REFRESHPERIOD) {
        refreshWhenEllapsed = MINIMUM_REFRESHPERIOD;
    }

    // If the ellapsedTime is below the refresh period then no need to refresh, exit.
    if (ellapsedMS < refreshWhenEllapsed) {
        return Promise.resolve(false); // No need to refresh now.
    }

    // Otherwise we will refresh...

    // Update refreshed time for future refresh period checks, so we don't call refresh too often.
    localStorage.setItem(STORAGEKEY_USERREFRESHED, currentMS);

    // Do the refresh.
    return api.auth.refresh(params).then(result => {
        if (!result.ok) {
            // Clear data.
            session.user.set(null);
            return false;
        }
        else {
            // Save user data.
            session.user.set(result.data);
            return true;
        }
    }).catch(ex => {
        log.error('lag.app.session._refreshUser{refresh-catch}', ex);
        session.user.set(null);
        return false;
    });
}

// Refresh the user data from API if a certain period of time has passed, to make sure the session data isn't stale.
function _refreshUserOnInterval() {
    clearInterval(hUserRefreshTimeout);

    // If no user then abort, there is no point in refreshing nothing.
    if (!session.user.valid)
        return;

    const period = session.user._cachedData.refreshPeriodMS;

    if (period < MINIMUM_REFRESHPERIOD) {
        log.error('lag.app.session._refreshUserOnInterval{small period}', period);
    }

    hUserRefreshTimeout = setInterval(_refreshUser, period);
}

// If the user changes (ie user signs-in/etc) then re-call _refreshUserOnInterval to reset the interval appropriately.
session.user.addChangeListener(_refreshUserOnInterval);
// If the user is already valid then start-up the refresh interval immediately.
if (session.user.valid) {
    _refreshUserOnInterval();
}

function _checkPermission(lookup, permissions) {
    // No permissions or invalid lookup, so deny.
    if (!permissions || !lookup || !lookup.length || !lookup.match(/^[a-z]+:([a-z][a-z0-9_]*[a-z0-9]\/?)+/)) {
        return false;
    }

    // Checks the specified permission lookup (should be similar to PermissionsSet.Check code),
    // the permission must exist, must be allowed (true), and must not have another entry of same lookup value or higher-level lookup value with deny (false)...

    let permissionAllowed = false; // Default to false (deny).

    for (const p in permissions)
    {
        // Check if the lookup is a match (exactly or as a child of the current permission), if not then ignore and continue to next permissions.
        if (!lookup.match(`^${p}(\/.+)?$`)) { continue; }

        // If it is a Deny permissions then deny, no need to check anymore permissions.
        // Deny permissions always superceed any allow permissions.
        if (permissions[p] == false) { return false; }
        else // Otherwise we have an allow permission, remember that but keep looping in case there is a deny permission that will superceed it.
        { permissionAllowed = true; }
    }

    // An allow was found without any superceeding deny or no matching permission was found and we default to deny.
    return permissionAllowed;
}